import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { r2DeletionService } from '../services/r2DeletionService';
import { videoHighlightsService } from '../../../shared/services/videoHighlightsService';

interface DeleteHighlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  highlightId: string;
  highlightName: string;
  hasStitched?: boolean;
  hasEnhanced?: boolean;
  onSuccess: () => void;
  onToast?: (message: string, type: 'success' | 'error') => void;
}

const DeleteHighlightModal: React.FC<DeleteHighlightModalProps> = ({
  isOpen,
  onClose,
  highlightId,
  highlightName,
  hasStitched: propHasStitched,
  hasEnhanced: propHasEnhanced,
  onSuccess,
  onToast,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteStitched, setDeleteStitched] = useState(false);
  const [deleteEnhanced, setDeleteEnhanced] = useState(false);
  const [hasStitched, setHasStitched] = useState(propHasStitched ?? false);
  const [hasEnhanced, setHasEnhanced] = useState(propHasEnhanced ?? false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Fetch highlight data if not provided via props
  useEffect(() => {
    if (isOpen && (propHasStitched === undefined || propHasEnhanced === undefined)) {
      setIsLoadingData(true);
      videoHighlightsService.getHighlightById(highlightId)
        .then((result) => {
          if (result.success && result.data) {
            setHasStitched(Boolean(result.data.output_storage_path || result.data.output_url));
            setHasEnhanced(Boolean(result.data.enhanced_storage_path || result.data.enhanced_url));
          }
        })
        .catch((err) => {
          console.error('Error fetching highlight data:', err);
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    } else {
      setHasStitched(propHasStitched ?? false);
      setHasEnhanced(propHasEnhanced ?? false);
    }
  }, [isOpen, highlightId, propHasStitched, propHasEnhanced]);

  // Reset checkboxes when modal opens
  useEffect(() => {
    if (isOpen) {
      setDeleteStitched(false);
      setDeleteEnhanced(false);
      setError(null);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    // Validation: at least one option must be selected
    if (!deleteStitched && !deleteEnhanced) {
      setError('Please select at least one video to delete');
      return;
    }

    setError(null);
    setIsDeleting(true);

    try {
      const deleteResult = await r2DeletionService.deleteHighlightAssets(highlightId, {
        deleteStitched,
        deleteEnhanced,
      });

      if (!deleteResult.success) {
        throw new Error(deleteResult.error || 'Failed to delete highlight assets');
      }

      // Show success toast
      const deletedItems: string[] = [];
      if (deleteStitched) deletedItems.push('stitched video');
      if (deleteEnhanced) deletedItems.push('enhanced video');

      const successMessage = deletedItems.length === 1
        ? `${deletedItems[0].charAt(0).toUpperCase() + deletedItems[0].slice(1)} deleted successfully`
        : `${deletedItems.join(' and ')} deleted successfully`;

      if (onToast) {
        onToast(successMessage, 'success');
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete highlight assets';
      setError(errorMessage);
      if (onToast) {
        onToast(errorMessage, 'error');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Highlight Videos">
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-700">
          Select which videos to delete for <strong>"{highlightName}"</strong>:
        </p>
        <p className="text-xs text-gray-500">
          This action cannot be undone. Selected videos will be permanently deleted from storage.
        </p>

        {isLoadingData && (
          <div className="flex items-center justify-center py-4">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-sm text-gray-500">Loading highlight data...</span>
          </div>
        )}

        {/* Checkboxes */}
        {!isLoadingData && (
          <div className="space-y-3 pt-2">
            <label className={`flex items-start space-x-3 ${hasStitched ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <input
                type="checkbox"
                checked={deleteStitched}
                onChange={(e) => setDeleteStitched(e.target.checked)}
                disabled={isDeleting || !hasStitched}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Delete Stitched Video</span>
                {hasStitched ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    The base/stitched video file will be permanently deleted from storage
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">
                    No stitched video available
                  </p>
                )}
              </div>
            </label>

            <label className={`flex items-start space-x-3 ${hasEnhanced ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <input
                type="checkbox"
                checked={deleteEnhanced}
                onChange={(e) => setDeleteEnhanced(e.target.checked)}
                disabled={isDeleting || !hasEnhanced}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Delete Enhanced Video</span>
                {hasEnhanced ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    The enhanced video (with overlays) will be permanently deleted from storage
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-0.5">
                    No enhanced video available
                  </p>
                )}
              </div>
            </label>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting || (!deleteStitched && !deleteEnhanced)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete Selected'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteHighlightModal;

